export type SLAStatus = 'on_time' | 'due_soon' | 'overdue' | 'breached' | 'paused' | 'completed';

export interface SLARule {
  priority_name: 'Low' | 'Medium' | 'High' | 'Critical';
  sla_hours: number;
  warning_threshold_pct: number; // 0-100, e.g. 75 means warning at 75% elapsed
  escalation_enabled: boolean;
  escalation_after_minutes: number; // minutes after breach to escalate
}

export interface SLAResult {
  status: SLAStatus;
  deadline: string;
  elapsed_pct: number;
  remaining_text: string;
  remaining_minutes: number;
}

export const DEFAULT_SLA_RULES: SLARule[] = [
  { priority_name: 'Critical', sla_hours: 4, warning_threshold_pct: 75, escalation_enabled: true, escalation_after_minutes: 30 },
  { priority_name: 'High', sla_hours: 8, warning_threshold_pct: 80, escalation_enabled: true, escalation_after_minutes: 60 },
  { priority_name: 'Medium', sla_hours: 24, warning_threshold_pct: 85, escalation_enabled: false, escalation_after_minutes: 0 },
  { priority_name: 'Low', sla_hours: 72, warning_threshold_pct: 90, escalation_enabled: false, escalation_after_minutes: 0 },
];

export function calculateSLA(
  createdAt: string,
  priorityName: string,
  rules: SLARule[] = DEFAULT_SLA_RULES,
  completedAt?: string,
): SLAResult {
  const rule = rules.find(r => r.priority_name === priorityName) || rules[2]; // default Medium
  const created = new Date(createdAt).getTime();
  const now = Date.now();
  const slaMs = rule.sla_hours * 60 * 60 * 1000;
  const deadline = new Date(created + slaMs);

  if (completedAt) {
    return {
      status: 'completed',
      deadline: deadline.toISOString(),
      elapsed_pct: 100,
      remaining_text: 'Completed',
      remaining_minutes: 0,
    };
  }

  const elapsedMs = now - created;
  const elapsedPct = Math.min(100, (elapsedMs / slaMs) * 100);
  const remainingMs = Math.max(0, deadline.getTime() - now);
  const remainingMin = Math.floor(remainingMs / 60000);

  let status: SLAStatus;
  if (remainingMs <= 0) {
    status = 'breached';
  } else if (elapsedPct >= rule.warning_threshold_pct) {
    status = 'due_soon';
  } else {
    status = 'on_time';
  }

  let remainingText: string;
  if (remainingMs <= 0) {
    const overMin = Math.floor(-remainingMs / 60000);
    if (overMin < 60) remainingText = `${overMin}m overdue`;
    else remainingText = `${Math.floor(overMin / 60)}h ${overMin % 60}m overdue`;
  } else if (remainingMin < 60) {
    remainingText = `${remainingMin}m remaining`;
  } else {
    const hrs = Math.floor(remainingMin / 60);
    const mins = remainingMin % 60;
    remainingText = `${hrs}h ${mins}m remaining`;
  }

  return { status, deadline: deadline.toISOString(), elapsed_pct: elapsedPct, remaining_text: remainingText, remaining_minutes: remainingMin };
}
